import { Component, OnInit, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { RouterOutlet, ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { DateAdapter } from '@angular/material/core';

import { AuthenticationService } from '../../../shared/src/lib/service/authentification.service';
import { DictionnaryService } from '../../../shared/src/lib/service/dictionnary.service';
import { AchievementManagerService } from '../../../shared/src/lib/service/achievementManager.service';
import { Student } from '../../../shared/src/lib/model/student.model';
import { AppStateStudent } from './student.states';
import { selectCurrentUser } from '../../../shared/src/lib/store/selectors/user.selectors';
import * as authActions from '../../../shared/src/lib/store/actions/auth.actions';

@Component({
  selector: 'app-student',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet],
  template: '<router-outlet></router-outlet>',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private authenticationService = inject(AuthenticationService);
  private dictionnaryService = inject(DictionnaryService);
  private translateService = inject(TranslateService);
  private achievementManagerService = inject(AchievementManagerService);
  private store = inject(Store<AppStateStudent>);
  private adapter = inject(DateAdapter);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private exchangedCode: string | null = null;

  constructor() {
    this.adapter.setLocale('fr');
  }

  ngOnInit(): void {
    const isImpersonate = typeof window !== 'undefined' && window.location.href.includes('impersonate=true');

    if (isImpersonate) {
      // Mode impersonate : on ne dispatch PAS login() ici.
      // Le store reste à isAuthenticated:false → le NoAuthFuseGuard laisse passer /impersonate.
      // Après le timeout (comme lilatefuse), on applique les nouveaux credentials puis on dispatch login().
      setTimeout(() => {
        this.activatedRoute.queryParamMap.subscribe(queryParams => {
          const code = queryParams.get('code');
          // Un code ne sert qu'une fois : les émissions suivantes l'ignorent.
          if (!code || code === this.exchangedCode) {
            return;
          }
          this.exchangedCode = code;
          this.clearPreviousSession();
          this.authenticationService.exchangeCode(code, { site: 'lingueopro', role: 'student' }).subscribe(() => {
            localStorage.setItem('site', 'lingueopro');
            localStorage.setItem('default_role', 'student');
            localStorage.setItem('impersonate', 'true');
            const formationId = queryParams.get('formation_id');
            if (formationId) {
              localStorage.setItem('formation_id', formationId);
              localStorage.setItem('last_selected_formation_id', formationId);
            }
            this.login();
            this.router.navigateByUrl('dashboard');
          });
        });
      }, 1000);
    } else {
      // Mode normal : on restaure la session depuis le localStorage
      this.login();
    }

    this.setTranslation();

    // Écouter le user connecté depuis le store pour les achievements
    this.store.select(selectCurrentUser)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user: Student | null) => {
        if (user) {
          // Initialiser les achievements si pas déjà fait
          if (this.achievementManagerService.achievementToFire.value.length === 0) {
            this.achievementManagerService.setAchievementsToFire(user);
          }
        }
      });
  }

  /**
   * Oublie la session précédente avant d'appliquer celle remise par le code
   * (enchaînement d'impersonations : ne pas rester sur le premier compte).
   */
  private clearPreviousSession() {
    ['persona', 'persona_obj', 'x-bundle-recurring', 'access_token', 'sign',
      'formation_id', 'last_selected_formation_id', 'impersonate'].forEach(key => localStorage.removeItem(key));
  }

  /**
   * Dispatch login action si tokens présents
   */
  login() {
    if (this.authenticationService.accessToken
      && this.authenticationService.xBundleRecurring) {
      this.store.dispatch(authActions.loginSuccess(
        {
          payload: {
            access_token: this.authenticationService.accessToken,
            xBundleRecurring: this.authenticationService.xBundleRecurring
          }
        }
      ));
    }
  }

  /**
   * Lancement de la traduction
   */
  private setTranslation() {
    const _lang = localStorage.getItem('language') || 'fr';
    this.translateService.setDefaultLang(_lang);
    this.translateService.use(_lang);
    this.dictionnaryService.getDictionnary2(_lang)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => {
        if (res) {
          this.translateService.setTranslation(_lang, res);
        }
      });
  }
}

